import { checkIfMatrixHasValidValues, getInitialMatrixData } from '@/components/data/entry/items/MatrixHelper';
import type { BlobModel } from '@/models/blob';
import { EntryModel, type EntryContext, type EntryUpdateReason, type PostEntry } from '@/models/entry';
import {
  FormFieldType,
  FormModel,
  type FormField,
  FormFieldChoice,
  type PostFormFile,
  type MatrixFieldUploadedFile,
  type MatrixTableRow
} from '@/models/form';
import { LoadingStateModel } from '@/models/loadingState';
import BlobService from '@/services/blobService';
import EntryService from '@/services/entryService';
import FormService from '@/services/formService';
import MetricsService from '@/services/metricsService';
import { defineStore } from 'pinia';
import { computed, ref, watch, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import mime from 'mime';
import { createEntryIdentifier, generateHexIdentifier, API_ERROR_CODES } from '@/utils';
import { computeEffectProps, getAssignDependencyIdentifiers, getFieldValue } from '@/components/data/entry/effects';
import { useFormStore } from './forms';
import { useStudyStore } from './study';
import type { SelectChangeEvent } from 'primevue/select';
import { usePipelineStore } from './pipeline';
import caseService from '@/services/caseService';
import { cloneDeep } from 'lodash';
import type { ActivityModel } from '@/models/activity';
import { useEntryListStore } from './entryList';
import { AxiosError } from 'axios';
import { uuid } from 'vue-uuid';
import { MetricsEvent } from '@/utils/metricsEvent';
import { awaitsDerivative, pollForDerivative } from '@/utils/derivativePoll';


export const useEntryStore = defineStore('entry', () => {

  const form = ref<FormModel>();
  const initialForm = ref<FormModel>();
  const entry = ref<EntryModel>();
  const entryActivities = ref<ActivityModel[]>();
  const entryLocked = ref();
  const route = useRoute();
  const selectedLanguage = ref();
  const formStore = useFormStore();
  const studyStore = useStudyStore();
  const pipelineStore = usePipelineStore();
  const entryListStore = useEntryListStore();
  const computeStarted = ref<boolean>(false);
  /**
   * Settles once this load's conditions pass has run, so anything that needs the *final* set of
   * fields can wait for it rather than race the timeout below.
   *
   * The pass is deferred on purpose (see `getForm`), which means that between the form resolving
   * and this settling, `form.items` still holds every conditionally hidden field. Anything that
   * counts, indexes or scrolls the rendered list in that window is working from a list that is
   * about to change under it — which is what sent the cell card's outstanding-field row to the top
   * of the form instead of to the field it named.
   *
   * Re-made per load, so a second entry opened in the same dialog waits for its own pass and not
   * for one that settled two entries ago.
   */
  const effectsSettled = ref<Promise<void>>(Promise.resolve());
  const entryTokenPreview = ref<boolean>(false);
  const copiedEntryData = ref<EntryModel | undefined>(undefined);
  const anonymousEntrySubmitError = ref<string>();
  const isAnonymousEntry = computed(() => route.fullPath.startsWith('/form/'));
  const entryAnalytics = ref<Array<{ event: string, timestamp: number, sessionId: string }>>([]);
  const formStartTracked = ref<boolean>(false);
  const entrySessionId = ref<string | null>(null);
  const multipleEntriesError = computed(() => form.value?.multipleEntriesError);
  const restoredEntryData = ref(false);
  let restoredDataTimeoutId: ReturnType<typeof setTimeout> | undefined;
  const formItemsWithoutValues = ref<FormField[]>();

  /**
   * Set by hosts that render the entry outside its own route (the pipeline's entry dialog), where the form and entry
   * identifiers live in the query rather than in route params. When unset, the route is the source of truth.
   */
  const contextOverride = ref<EntryContext>();

  const entryParams = computed<EntryContext>(() => contextOverride.value ?? {
    formIdentifier: route.params?.formIdentifier as string,
    entryIdentifier: route.params?.entryIdentifier as string | undefined,
    caseIdentifier: (route.params?.identifier ?? route.query?.cid) as string | undefined,
    token: route.query?.token as string | undefined
  });

  const setEntryContext = (context: EntryContext) => {
    contextOverride.value = context;
  };

  const clearEntryContext = () => {
    contextOverride.value = undefined;
  };

  const caseId = computed(() => entryParams.value.caseIdentifier);
  const entryToken = computed(() => entryParams.value.token);

  /**
   * The case whose details are loaded and shown alongside the entry. Anonymous entries carry a `cid` for attribution
   * only and must not fetch case details, which they have no permission to read — hence the narrower source than
   * `caseId`.
   */
  const selectedCaseIdentifier = computed(() => contextOverride.value ?
    contextOverride.value.caseIdentifier :
    route.params?.identifier as string | undefined);

  const getFormLoader = ref(new LoadingStateModel({ isLoading: false }));
  const formClosedError = ref(false);
  const formClosedMessage = ref('');
  const anonymousResponsesNotAllowed = ref(false);
  const getForm = async () => {
    try {
      initialForm.value = undefined;
      form.value = undefined;
      formClosedError.value = false;
      formClosedMessage.value = '';
      anonymousResponsesNotAllowed.value = false;
      entry.value = undefined;
      selectedFiles.value = {};
      entryAnalytics.value = [];
      formStartTracked.value = false;
      entrySessionId.value = uuid.v4();
      isEntryCloseEventSent.value = false;
      pendingCloseEventData.value = null;
      /** The entry can be re-opened without the component remounting (the pipeline's entry dialog), so every piece of
       * per-entry state has to be cleared here rather than relying on a fresh store. */
      entryLocked.value = undefined;
      /* Resolved, not pending: `getForm` has several early returns that never reach the conditions
         pass, and a waiter must not be left hanging on one of them. The real pending promise
         replaces this the moment that pass is scheduled. */
      effectsSettled.value = Promise.resolve();
      entryTokenPreview.value = false;
      entryActivities.value = undefined;
      anonymousEntrySubmitError.value = undefined;
      selectedMatrixFieldFiles.value = [];
      oldFormItems.value = undefined;
      hideRestoredEntryDataMsg();
      getFormLoader.value.startLoading();
      const { formIdentifier, entryIdentifier, caseIdentifier, token } = entryParams.value;
      form.value = await FormService.getForm(
        formIdentifier,
        caseIdentifier,
        studyStore.selectedStudyId,
        token
      );
      if (form.value?.closedMessage) {
        formClosedError.value = true;
        formClosedMessage.value = form.value.closedMessage;
        return;
      }
      formItemsWithoutValues.value = cloneDeep(form.value.items);
      if (form.value?.showSaveEntryButtonAtBottom) {
        addClientField(FormFieldType.SUBMIT_BUTTON);
      }
      if (isAnonymousEntry.value) {
        addClientField(FormFieldType.BRANDING);
      }
      selectedLanguage.value = form.value?.formTranslations ? form.value?.formTranslations[0] : undefined;
      if (entryIdentifier) {
        entry.value = await EntryService.getEntry(entryIdentifier, form.value);
        entryLocked.value = entry.value.lockedAt;
      } else if (token && form.value?.editExistingEntry) {
        const entryWithToken = await EntryService.getEntryByToken(token, form.value);
        if (entryWithToken) {
          entry.value = entryWithToken;
          entryLocked.value = entry.value.lockedAt;
          entryTokenPreview.value = true;
        } else {
          setInitialValuesForNewEntry();
        }
      } else if (copiedEntryData.value) {
        EntryModel.mapWithFormItems(form.value?.items, copiedEntryData.value.items);
        copiedEntryData.value = undefined;
      } else {
        setInitialValuesForNewEntry();

        // Check for cached public entry in local storage
        if (form.value?.cachePublicEntry) {
          const cachedEntryData = localStorage.getItem(`publicEntry_${form.value?.identifier}`);
          if (cachedEntryData) {
            restoredEntryData.value = true;
            if (restoredDataTimeoutId) {
              clearTimeout(restoredDataTimeoutId);
            }
            restoredDataTimeoutId = setTimeout(() => {
              restoredEntryData.value = false;
            }, 60000);
            try {
              const parsedData = JSON.parse(cachedEntryData);
              if (parsedData && parsedData.items) {
                form.value!.items = parsedData.items;
              }
            } catch (e) {
              // Clear invalid data
              // eslint-disable-next-line no-console
              console.error('Failed to restore cached public entry data', {
                formIdentifier: form.value?.identifier,
                error: e
              });
            }
          }
        }
      }
      if (selectedCaseIdentifier.value && form.value) {
        form.value.selectedCase =
          pipelineStore.caseList.find(caseItem => caseItem.identifier == selectedCaseIdentifier.value) ??
          (entryListStore.selectedCase?.identifier === selectedCaseIdentifier.value ?
            entryListStore.selectedCase :
            undefined);
        /** Neither store holds the case when the entry was deep-linked, or when the case sits on another page of the
         * pipeline — fetch it so the header and the case notes still have something to show. */
        if (!form.value.selectedCase) {
          form.value.selectedCase = await caseService.getCaseEntries(selectedCaseIdentifier.value);
        }
      }

      getFilesMetadata();
      initialForm.value = cloneDeep(form.value);
      /**
       * computeEffects(): This handles computing effects after form initialization, ensuring the SHOW effect runs at least once during
       * initialization. However, a drawback is that the ASSIGN effect also runs, which is unintended. The ASSIGN effect
       * performs calculations within the form, and we want to avoid these calculations until some data has been entered.
       * For now, we can overlook this issue, but a proper solution needs to be implemented in the future.
       */
      effectsSettled.value = new Promise<void>(resolve => {
        /** We're using `setTimeout` to ensure all fields are initially visible, allowing the Virtualizer to allocate space in the view.
         * After that, `computeEffects` runs, and the conditions are applied. */
        setTimeout(() => {
          computeEffects();
          initialForm.value = cloneDeep(form.value);
          resolve();
        }, 10);
      });

      //set form name
      document.title = `${form.value?.title} | Studypages Data`;

      const openEventTimestamp = Date.now();
      entryAnalytics.value.push({
        event: MetricsEvent.FORM_ENTRY_OPEN,
        timestamp: openEventTimestamp,
        sessionId: entrySessionId.value!
      });

      if (form.value?.identifier) {
        MetricsService.recordEntryMetrics({
          entryId: '',
          sessionId: entrySessionId.value!,
          studyId: studyStore.selectedStudyId || form.value.studyId || '',
          formId: form.value.identifier,
          event: MetricsEvent.FORM_ENTRY_OPEN,
          timestamp: openEventTimestamp,
          entryToken: entryToken.value,
          caseId: caseId.value
        }).catch(() => { });
      }

      //setting default values
      if (!entry.value) {
        setDefaltValues();
      }
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response?.status === 401 &&
        error.response?.data?.error_code === API_ERROR_CODES.ANONYMOUS_RESPONSES_NOT_ALLOWED
      ) {
        anonymousResponsesNotAllowed.value = true;
      } else {
        getFormLoader.value.setError(error);
      }
    } finally {
      getFormLoader.value.stopLoadingAndFinish();
    }
  };

  const resetRestoredEntryData = () => {
    form.value?.items?.forEach(field => {
      field.value = undefined;
      field.numberValue = undefined;
      if (field.type === FormFieldType.CHOICE) {
        field.options.choices?.forEach(choice => choice.checked = false);
        field.choiceValue = FormFieldChoice.createEmpty();
        field.otherChoiceValue = undefined;
      }
      if (field.type === FormFieldType.MATRIX) {
        field.matrixValues = [];
        field.matrixTableRowValues = getInitialMatrixData(field as FormField);
      }
    });
    restoredEntryData.value = false;
    if (restoredDataTimeoutId) {
      clearTimeout(restoredDataTimeoutId);
    }
  };

  const hideRestoredEntryDataMsg = () => {
    restoredEntryData.value = false;
    if (restoredDataTimeoutId) {
      clearTimeout(restoredDataTimeoutId);
    }
  };

  const addClientField = (fieldType: FormFieldType) => {
    form.value?.items?.push({
      id: '',
      type: fieldType,
      identifier: generateHexIdentifier(6),
      label: '',
      description: '',
      strictRequired: false,
      nonStrictRequired: false,
      show: true,
      disable: false,
      inputChanged: false,
      options: {},
      default: '',
      formulaString: '',
      order: form.value.items ? form.value.items.length + 1 : 0,
      expanded: true
    });
  };

  const oldFormItems = ref();

  watch(() => form.value?.items, () => {
    // only compute effects if there has been changes made
    if (
      form.value &&
      JSON.stringify(form.value?.items) !== JSON.stringify(oldFormItems.value) &&
      !getFormLoader.value.isLoading &&
      (!isEntryPristine.value || computeStarted.value)
    ) {
      computeEffects();
    }
    if (!isEntryPristine.value) {
      trackFormStart();
    }
    oldFormItems.value = form.value?.items ? cloneDeep(form.value?.items) : undefined;
  }, { deep: true });

  const trackFormStart = async () => {
    if (!formStartTracked.value && entrySessionId.value) {
      formStartTracked.value = true;

      const startTimestamp = Date.now();
      entryAnalytics.value.push({
        event: MetricsEvent.FORM_ENTRY_START,
        timestamp: startTimestamp,
        sessionId: entrySessionId.value
      });

      if (form.value?.identifier) {
        await MetricsService.recordEntryMetrics({
          entryId: entryParams.value.entryIdentifier || entry.value?.identifier || '',
          sessionId: entrySessionId.value,
          studyId: studyStore.selectedStudyId || form.value.studyId || '',
          formId: form.value.identifier,
          event: MetricsEvent.FORM_ENTRY_START,
          timestamp: startTimestamp,
          entryToken: entryToken.value,
          caseId: caseId.value
        }).catch(() => { });
      }
    }
  };

  /** The other fields each field's `Assign` calculation reads, keyed by field identifier. */
  const getAssignDependencies = (fields: FormField[]) => {
    return new Map(fields.map(field => [
      field.identifier,
      getAssignDependencyIdentifiers(field.conditions).filter(identifier => identifier !== field.identifier)
    ]));
  };

  /**
   * Identifiers of the fields whose value can be considered "changed" for this compute pass: the fields the user has
   * edited, plus the fields that calculate their own value from one of those (so chains of calculated fields keep
   * working even when assigning a value doesn't flag the target field as changed, e.g. dates).
   */
  const getChangedFieldIdentifiers = (fields: FormField[], assignDependencies: Map<string, string[]>) => {
    const changedIdentifiers = new Set(fields.filter(field => field.inputChanged).map(field => field.identifier));

    let resolvedNewDependent = true;
    while (resolvedNewDependent) {
      resolvedNewDependent = false;
      for (const field of fields) {
        if (
          !changedIdentifiers.has(field.identifier) &&
          assignDependencies.get(field.identifier)!.some(identifier => changedIdentifiers.has(identifier))
        ) {
          changedIdentifiers.add(field.identifier);
          resolvedNewDependent = true;
        }
      }
    }

    return changedIdentifiers;
  };

  const computeEffects = () => {
    computeStarted.value = true;
    const fields = form.value?.items || [];
    const assignDependencies = getAssignDependencies(fields);
    const changedIdentifiers = getChangedFieldIdentifiers(fields, assignDependencies);

    fields.forEach(field => {
      /** A calculation that depends on other fields must not run until at least one of them has actually been edited,
       * otherwise opening an entry recalculates (and therefore dirties) it on its own. */
      const dependencies = assignDependencies.get(field.identifier)!;
      const skipAssign = dependencies.length > 0 && !dependencies.some(identifier => changedIdentifiers.has(identifier));

      computeEffectProps(field, fields, { skipAssign });

      // finding matrix fields that are dependent on other field's value
      if (field.type == FormFieldType.MATRIX) {
        const conditions = field.options.rows?.filter(row => row.dynamicRowConfig?.conditions);
        if (conditions) {
          const matches = JSON.stringify(conditions).match(/"@([^"]+)"/g)?.map(m => m.slice(2, -1)) || [];

          for (const matchedId of matches) {
            const matchedField = form.value?.items?.find(item => item.identifier == matchedId);
            if (matchedField?.inputChanged) {
              field.matrixDependencyResolved = field.matrixDependencyResolved ? field.matrixDependencyResolved + 1 : 1;
            }
          }
        }
      }
    });
  };

  const selectedFiles = ref<Record<string, BlobModel[]>>({});

  const getFilesMetadata = async () => {
    for (const field of form.value!.items!) {
      if (field.type === FormFieldType.FILE) {
        const files: BlobModel[] = [];
        if (field.fileIds) {
          for (const fileId of field.fileIds) {
            const file = await BlobService.getBlobMetadata(fileId);
            if (file) {
              files.push(file);
            }
          }
        }

        if (files.length > 0) {
          selectedFiles.value![field.identifier] = files;
          files.filter(awaitsDerivative).forEach(file => watchFieldFile(field.identifier, file.identifier));
        }
      }

      if (field.type === FormFieldType.MATRIX && field.matrixTableRowValues) {
        for (const row of field.matrixTableRowValues) {
          for (const column of row.columnValues || []) {
            if (column.fileIds) {
              const files: BlobModel[] = [];
              for (const fileId of column.fileIds) {
                const file = await BlobService.getBlobMetadata(fileId);
                if (file) {
                  files.push(file);
                }
              }
              if (files.length > 0) {
                const cell = { identifier: field.identifier, rowId: row.rowId, columnId: `${column.id}` };
                selectedMatrixFieldFiles.value.push({ ...cell, files });
                files.filter(awaitsDerivative).forEach(file => watchMatrixFile(cell, file.identifier));
              }
            }
          }
        }
      }
    }
  };

  const downloadBlob = (indetifier: string, fileName: string) => {
    BlobService.downloadBlob(indetifier, fileName);
  };

  const downloadDisplayCopy = (identifier: string, fileName: string) => {
    BlobService.downloadDisplayCopy(identifier, fileName);
  };

  /**
   * Refetches a blob until the worker's thumbnail lands, and swaps each answer into the list
   * holding it. Nothing else would notice it arriving, because a field's files are read once, when
   * the entry opens. Stops once the file leaves that list. A delete does that, and so does opening
   * another entry, since `getForm` replaces both lists.
   */
  const watchForDerivative = (blobIdentifier: string, holdingList: () => BlobModel[] | undefined) => {
    const indexInList = () => holdingList()?.findIndex(file => file.identifier === blobIdentifier) ?? -1;
    void pollForDerivative({
      fetch: () => BlobService.getBlobMetadata(blobIdentifier),
      stillWanted: () => indexInList() > -1,
      onAnswer: answer => {
        const index = indexInList();
        if (index < 0) {
          return;
        }
        const files = holdingList()!;
        answer.localPreviewUrl = files[index].localPreviewUrl;
        files.splice(index, 1, answer);
      }
    });
  };

  const watchFieldFile = (fieldIdentifier: string, blobIdentifier: string) =>
    watchForDerivative(blobIdentifier, () => selectedFiles.value[fieldIdentifier]);

  const watchMatrixFile = (cell: Omit<MatrixFieldUploadedFile, 'files'>, blobIdentifier: string) =>
    watchForDerivative(blobIdentifier, () => selectedMatrixFieldFiles.value.find(held =>
      held.identifier === cell.identifier && held.rowId === cell.rowId && `${held.columnId}` === `${cell.columnId}`
    )?.files);

  const deleteFile = (fieldIdentifier: string, identifier: string) => {
    const files = selectedFiles.value[fieldIdentifier];
    const fileIndex = files.findIndex(file => file.identifier == identifier);
    if (fileIndex > -1) {
      files.splice(fileIndex, 1);
    }
    selectedFiles.value[fieldIdentifier] = files;
    form.value!.items!.find(field => field.identifier === fieldIdentifier)!.fileIds = files.map(file => file.identifier);
  };

  const fileUploadLoader = ref(new LoadingStateModel({ isLoading: false }));
  const getSignedUrl = async (identifier: string) => {
    try {
      fileUploadLoader.value.startLoading();

      const files = selectedFiles.value![identifier];

      const updatedFiles: BlobModel[] = [];
      const awaitingDerivatives: string[] = [];
      for (const file of files) {
        if (!file.storedAt) {
          const fileData: PostFormFile = {
            filename: file.name,
            filetype: file.contentType,
            size: file.size,
            withoutItemId: true
          };
          const presignedData = await FormService.postFormFile(form.value!.identifier, fileData);
          await FormService.uploadFileToS3WithProgress(file, presignedData.presignedPost);
          const updatedFile = await BlobService.patchBlob(presignedData.blob.identifier);

          if (file.file && mime.getType(file.file.name)?.includes('image')) {
            updatedFile.file = file.file;
            // @ts-expect-error objectURL is the actual path of the file
            updatedFile.localPreviewUrl = updatedFile.file.objectURL;
          }
          updatedFiles.push(updatedFile);
          if (awaitsDerivative(updatedFile)) {
            awaitingDerivatives.push(updatedFile.identifier);
          }
        } else {
          updatedFiles.push(file);
        }
      }
      selectedFiles.value![identifier] = updatedFiles;
      form.value!.items!.find(field => field.identifier === identifier)!.fileIds = updatedFiles.map(file => file.identifier);
      awaitingDerivatives.forEach(blobIdentifier => watchFieldFile(identifier, blobIdentifier));
    } catch (error) {
      fileUploadLoader.value.setError(error);
    } finally {
      fileUploadLoader.value.stopLoadingAndFinish();
    }
  };

  const deleteEffectsVariables = (field: FormField) => {
    delete field.show;
    delete field.warn;
    delete field.error;
    delete field.valid;
    delete field.disable;
    delete field.strictRequired;
    delete field.nonStrictRequired;
    delete field.showRequiredError;
    delete field.inputChanged;
    delete field.matrixDependencyResolved;
    delete field.options.dateShifting;
    delete field.sectionExpanded;
    delete field.expanded;
    delete field.blink;

    field.options.columns?.forEach(column => {
      delete column.options?.showingShiftedDate;
    });

    field.matrixTableRowValues?.forEach(row => {
      row.columnValues?.forEach(column => {
        delete column.unShiftedDate;
      });
    });
  };

  const isEntryPristine = computed(() => {
    const oldForm: FormModel | undefined = cloneDeep(initialForm.value);
    oldForm?.items?.forEach(item => deleteEffectsVariables(item));
    delete oldForm?.selectedCase;
    removeMatrixColumnEmptyConditions(oldForm?.items || []);
    const newForm: FormModel | undefined = cloneDeep(form.value);
    newForm?.items?.forEach(item => deleteEffectsVariables(item));
    delete newForm?.selectedCase;
    removeMatrixColumnEmptyConditions(newForm?.items || []);
    if (!oldForm || !newForm) {
      return true;
    }
    return JSON.stringify(oldForm) === JSON.stringify(newForm);
  });

  const removeMatrixColumnEmptyConditions = (fields: FormField[]) => {
    for (const field of fields) {
      if (field.type === FormFieldType.MATRIX && field.matrixTableRowValues) {
        field.matrixTableRowValues.forEach(row => {
          row.columnValues?.forEach(column => {
            if (column.conditions) {
              delete column.conditions;
            }
          });
        });
      }
    }
  };

  const openDiscardChangesAlert = ref(false);
  const resolveFn = ref();
  const rejectFn = ref();

  const showDiscardChangesDialog = async () => {
    openDiscardChangesAlert.value = true;

    return new Promise((resolve, reject) => {
      resolveFn.value = resolve;
      rejectFn.value = reject;
    });
  };

  const cancelDiscardChangesDialog = () => {
    openDiscardChangesAlert.value = false;
    /** Reject so the caller (the route guard, or the entry dialog) knows to stay put. Clearing `rejectFn` without
     * calling it would leave the pending navigation unresolved forever. */
    rejectFn.value?.();
    rejectFn.value = undefined;
    resolveFn.value = undefined;
  };

  const discardEntryChanges = () => {
    openDiscardChangesAlert.value = false;
    rejectFn.value = undefined;
    form.value = undefined;
    formStartTracked.value = false;
    setTimeout(() => {
      // Adding a timeout since the `form.value = undefined` alone wasn't helping with the reactivity.
      form.value = cloneDeep(initialForm.value);
      resolveFn.value?.();
      resolveFn.value = undefined;
    }, 1);
  };

  /** Resolves to whether the entry was persisted, so hosts that stay mounted (the entry dialog) can tell a failed save
   * from a successful one — errors are reported through the loader and not rethrown. */
  const submitEntry = async (navigate?: boolean, reason?: EntryUpdateReason): Promise<boolean> => {
    let submitted = false;
    anonymousEntrySubmitError.value = '';
    const updatedItems: Record<string, string | number> = {};
    getFormLoader.value.startLoading();
    form.value?.mapItemValues(updatedItems);
    const formId = form.value?.identifier;
    const { entryIdentifier, caseIdentifier } = entryParams.value;

    if (form.value?.identifier && entrySessionId.value) {
      const submissionTimestamp = Date.now();
      await MetricsService.recordEntryMetrics({
        entryId: entryIdentifier || '',
        sessionId: entrySessionId.value,
        studyId: studyStore.selectedStudyId || form.value.studyId || '',
        formId: form.value.identifier,
        event: MetricsEvent.FORM_ENTRY_SUBMIT,
        timestamp: submissionTimestamp,
        entryToken: entryToken.value,
        caseId: caseId.value
      }).catch(() => { });
    }

    const payload: PostEntry = {
      studyId: studyStore.selectedStudyId || form.value?.studyId,
      items: updatedItems,
      revisionDate: new Date().getTime(),
      entryToken: entryToken.value,
      reason
    };

    hideRestoredEntryDataMsg();

    try {
      if (entryIdentifier || entry.value) {
        const entryId = entry.value!.identifier;
        await EntryService.updateEntry(entryId, payload);
        getFormLoader.value.setSuccess('Entry updated successfully');

        if (form.value?.identifier && entrySessionId.value) {
          await MetricsService.recordEntryMetrics({
            entryId: entryId,
            sessionId: entrySessionId.value,
            studyId: studyStore.selectedStudyId || form.value.studyId || '',
            formId: form.value.identifier,
            event: MetricsEvent.FORM_ENTRY_SAVE,
            timestamp: Date.now(),
            entryToken: entryToken.value,
            caseId: caseId.value
          }).catch(() => { });
        }

        if (form.value?.identifier && entrySessionId.value && !isEntryCloseEventSent.value) {
          pendingCloseEventData.value = {
            entryId: entry.value?.identifier || '',
            sessionId: entrySessionId.value,
            studyId: studyStore.selectedStudyId || form.value.studyId || '',
            formId: form.value.identifier
          };
        }

        if (navigate) {
          resolveFn.value?.();
          resolveFn.value = undefined;

          form.value = undefined;
          form.value = cloneDeep(initialForm.value);
        }

        await getForm();
      } else {
        const entryId = createEntryIdentifier(form.value!.identifier);
        payload.uri = `response/${entryId}`;
        if (caseIdentifier) {
          payload.caseUri = `case/${caseIdentifier}`;
        }
        const entryRes = await EntryService.createEntry(payload);
        getFormLoader.value.setSuccess('Entry created successfully');
        const newEntryIdentifier = entryRes.headers.location.split('/').pop() || '';
        if (form.value?.identifier && entrySessionId.value) {
          await MetricsService.recordEntryMetrics({
            entryId: newEntryIdentifier,
            sessionId: entrySessionId.value,
            studyId: studyStore.selectedStudyId || form.value.studyId || '',
            formId: form.value.identifier,
            event: MetricsEvent.FORM_ENTRY_SAVE,
            timestamp: Date.now(),
            entryToken: entryToken.value,
            caseId: caseId.value
          }).catch(() => { });
        }

        if (form.value?.identifier && entrySessionId.value && !isEntryCloseEventSent.value) {
          pendingCloseEventData.value = {
            entryId: newEntryIdentifier,
            sessionId: entrySessionId.value,
            studyId: studyStore.selectedStudyId || form.value.studyId || '',
            formId: form.value.identifier
          };
        }

        form.value = undefined;
        initialForm.value = undefined;
        if (navigate) {
          resolveFn.value?.();
          resolveFn.value = undefined;
        }

        getFormLoader.value.stopLoadingAndFinish();
      }
      localStorage.removeItem(
        `publicEntry_${formId}`
      );
      submitted = true;
    } catch (error) {
      getFormLoader.value.setError(error);
      if (route.fullPath.startsWith('/form/')) {
        if (error instanceof AxiosError) {
          anonymousEntrySubmitError.value = error.response?.data.message ?? error.response?.data.error ?? (error as Error).message;
        } else {
          anonymousEntrySubmitError.value = (error as Error).message;
        }
      }
    } finally {
      getFormLoader.value.stopLoadingAndFinish();
      formStore.getStudyForms();
    }

    return submitted;
  };

  const setDefaltValues = () => {
    for (const field of form.value!.items!) {
      if (field.default) {
        switch (field.type) {
          case FormFieldType.TEXT:
            field.value = field.default;
            break;

          case FormFieldType.CHOICE:
            field.choiceValue = field.options.choices?.find(choice => choice.value == field.default);
            break;
        }
      }
    }
  };

  const setInitialValuesForNewEntry = () => {
    for (const field of form.value!.items!) {

      switch (field.type) {
        case FormFieldType.TEXT:
          field.value = '';
          break;

        case FormFieldType.NUMBER:
          field.numberValue = null;
          break;

        case FormFieldType.FILE:
          selectedFiles.value[field.identifier] = [];
          break;

        case FormFieldType.CHOICE:
          if (field.options.maximumChoicesCount == 1) {
            field.choiceValue = FormFieldChoice.createEmpty();
          } else {
            field.options.choices?.forEach(choice => choice.checked = false);
          }
          break;

        case FormFieldType.DROPDOWN:
          if (field.options.maximumChoicesCount == 1) {
            // @ts-expect-error just initialising the null value
            field.choiceValue = null;
          } else {
            field.choiceValue = [];
          }
          break;

        case FormFieldType.MATRIX:
          field.matrixValues = [];

          field.matrixTableRowValues = getInitialMatrixData(field);
          break;

        case FormFieldType.DRILLDOWN:
          field.drillValue = [];
          break;

        case FormFieldType.UNIT:
          // eslint-disable-next-line no-case-declarations
          const defaultUnit = field.options.units?.list.find(unitItem => unitItem.id === field.options.units!.default);
          field.value = defaultUnit?.unit;
          if (field.options.units?.list.length === 1) {
            field.value = field.options.units?.list[0].unit;
          }
          break;

        case FormFieldType.SECTION:
          if (field.options.collapsedInitially) {
            field.sectionExpanded = false;

            const sectionFields = field.options.fields;
            sectionFields?.forEach(fieldItem => {
              const foundField = form.value?.items!.find(item => item.identifier === fieldItem);
              if (foundField) {
                foundField.expanded = field.sectionExpanded;
              }
            });
          }
          break;
        default:
          break;
      }
    }
  };

  const switchLangauge = (event: SelectChangeEvent) => {
    for (const translatedField of event.value.items!) {
      for (const field of form.value!.items!) {
        if (field.identifier === translatedField.identifier) {
          field.label = translatedField.label;
          field.description = translatedField.description;

          const initialFormField = initialForm.value?.items?.find(item => item.identifier == field.identifier);

          initialFormField!.label = field.label;
          initialFormField!.description = field.description;

          if (translatedField.options.choices) {
            for (const translatedChoice of translatedField.options.choices) {
              for (const choice of field.options.choices!) {
                if (choice.value == translatedChoice.value) {
                  choice.label = translatedChoice.label;
                  break;
                }
              }

              for (const choice of initialFormField!.options.choices!) {
                if (choice.value == translatedChoice.value) {
                  choice.label = translatedChoice.label;
                  break;
                }
              }
            }
          }

          // manage matrix field translations
          if (translatedField.options.columns) {
            for (const translatedColumn of translatedField.options.columns) {
              for (const column of field.options.columns!) {
                if (column.id == translatedColumn.id) {
                  column.label = translatedColumn.label;
                  break;
                }
              }
            }

            for (const translatedColumn of translatedField.options.columns) {
              for (const column of initialFormField!.options.columns!) {
                if (column.id == translatedColumn.id) {
                  column.label = translatedColumn.label;
                  break;
                }
              }
            }
          }

          if (translatedField.options.rows) {
            for (const translatedRow of translatedField.options.rows) {
              for (const tableRow of field.matrixTableRowValues!) {
                if (`${translatedRow.id}` === tableRow.rowId) {
                  tableRow.rowHeader = translatedRow.label;
                  break;
                }

              }
            }

            for (const translatedRow of translatedField.options.rows) {
              for (const tableRow of initialFormField!.matrixTableRowValues!) {
                if (`${translatedRow.id}` === tableRow.rowId) {
                  tableRow.rowHeader = translatedRow.label;
                  break;
                }

              }
            }
          }

          break;
        }

      }
    }
  };

  const lookForRequiredFields = () => {
    let requiredUnansweredFields = 0;
    form.value?.items?.forEach(field => {
      if (field.strictRequired && field.show !== false) {
        const fieldValue = getFieldValue(field);
        if (
          field.type === FormFieldType.MATRIX &&
          fieldValue
        ) {
          const matrixRows = fieldValue as MatrixTableRow[];
          const validValue = checkIfMatrixHasValidValues(field, matrixRows);
          if (!validValue) {
            field.showRequiredError = true;
            field.inputChanged = true;
            requiredUnansweredFields++;
          }
        } else if (fieldValue == null || fieldValue === undefined || fieldValue === '') {
          field.showRequiredError = true;
          field.inputChanged = true;
          requiredUnansweredFields++;
        } else {
          field.showRequiredError = false;
        }
      }

      if (field.nonStrictRequired) {
        const fieldValue = getFieldValue(field);
        if (!fieldValue) {
          field.showRequiredError = true;
          if (field.type !== FormFieldType.UNIT) {
            field.inputChanged = true;
          }
        }
      }

      // TODO: Confirm if the fields with Error Effect should restrict user from saving the form
    });
    return requiredUnansweredFields;
  };

  /** Opens or closes a section, taking its fields with it. Every caller says which it wants rather than toggling:
   * only the Expand button is ever closing one, and all the rest are opening a section to reveal a field inside it —
   * which a toggle got wrong whenever that section was open already. */
  const setSectionExpanded = (formField: FormField, expanded: boolean) => {
    formField.sectionExpanded = expanded;
    const sectionFields = formField.options.fields;
    sectionFields?.forEach(field => {
      const foundField = form.value?.items?.find(item => item.identifier === field);
      if (foundField) {
        foundField.expanded = expanded;
      }
    });
  };

  const fetchEntrySignHistory = async () => {
    if (entry.value?.identifier) {
      entryActivities.value = await EntryService.getEntryActivities(entry.value.identifier);
      // entry.value.signHistory = signHistory;
    }
  };

  const selectedMatrixFieldFiles = ref<MatrixFieldUploadedFile[]>([]);

  const getSignedUrlForMatrixFiles = async (matrixFileData: MatrixFieldUploadedFile) => {
    try {
      const matrixFieldFileData = selectedMatrixFieldFiles.value.find(file =>
        file.identifier === matrixFileData.identifier && matrixFileData.rowId === file.rowId && `${matrixFileData.columnId}` === file.columnId
      );
      const files = matrixFieldFileData?.files || [];
      const updatedFiles: BlobModel[] = [];
      const awaitingDerivatives: string[] = [];
      for (const file of files) {
        if (!file.storedAt) {
          const fileData: PostFormFile = {
            filename: file.name,
            filetype: file.contentType,
            size: file.size,
            withoutItemId: true
          };
          const presignedData = await FormService.postFormFile(form.value!.identifier, fileData);
          await FormService.uploadFileToS3WithProgress(file, presignedData.presignedPost);
          const updatedFile = await BlobService.patchBlob(presignedData.blob.identifier);

          if (file.file && mime.getType(file.file.name)?.includes('image')) {
            updatedFile.file = file.file;
            // @ts-expect-error objectURL is the actual path of the file
            updatedFile.localPreviewUrl = updatedFile.file.objectURL;
          }
          updatedFiles.push(updatedFile);
          if (awaitsDerivative(updatedFile)) {
            awaitingDerivatives.push(updatedFile.identifier);
          }
        } else {
          updatedFiles.push(file);
        }
      }
      if (matrixFieldFileData) {
        matrixFieldFileData.files = updatedFiles;
        awaitingDerivatives.forEach(blobIdentifier => watchMatrixFile(matrixFieldFileData, blobIdentifier));
      }
      const matrixColumn = form.value!.items!.find(field => field.identifier === matrixFileData.identifier)?.matrixTableRowValues
        ?.find(row => row.rowId === matrixFileData.rowId)?.columnValues?.find(column => `${column.id}` === matrixFileData.columnId);

      if (matrixColumn && updatedFiles.length > 0) {
        matrixColumn.fileIds = updatedFiles.map(file => file.identifier);
        matrixColumn.textValue = JSON.stringify(matrixColumn.fileIds);
      }
    } catch (error) {
      fileUploadLoader.value.setError(error);
    } finally {
      fileUploadLoader.value.stopLoadingAndFinish();
    }
  };

  const deleteMatrixFieldFile = (identifier: string, matrixFileData: MatrixFieldUploadedFile) => {
    const matrixFieldFileData = selectedMatrixFieldFiles.value.find(file =>
      file.identifier === matrixFileData.identifier && matrixFileData.rowId === file.rowId && `${matrixFileData.columnId}` === file.columnId
    );
    const files = matrixFieldFileData?.files || [];
    const fileIndex = files.findIndex(file => file.identifier == identifier);
    if (fileIndex > -1) {
      files.splice(fileIndex, 1);
    }
    if (matrixFieldFileData) {
      matrixFieldFileData.files = files;
    }
    const matrixColumn = form.value!.items!.find(field => field.identifier === matrixFileData.identifier)?.matrixTableRowValues
      ?.find(row => row.rowId === matrixFileData.rowId)?.columnValues?.find(column => `${column.id}` === matrixFileData.columnId);
    if (matrixColumn) {
      matrixColumn.fileIds = files.map(file => file.identifier);
      matrixColumn.textValue = JSON.stringify(matrixColumn.fileIds);
    }
  };

  const entryDisableEdit =
    computed(() => form.value?.hasEntryPermission === false ? 'You do not have permission to edit this form' : undefined);

  const isEntryCloseEventSent = ref(false);
  const pendingCloseEventData = ref<{
    entryId: string;
    sessionId: string;
    studyId: string;
    formId: string;
  } | null>(null);

  const cleanupEntryCloseListener = ref<(() => void) | null>(null);

  const cleanupListeners = () => {
    if (cleanupEntryCloseListener.value) {
      cleanupEntryCloseListener.value();
      cleanupEntryCloseListener.value = null;
    }
  };

  const sendEntryCloseEvent = () => {
    if (isEntryCloseEventSent.value) {
      return;
    }

    let entryId = '';
    let sessionId: string | null = null;
    let studyId: string | null = null;
    let formId: string | undefined = undefined;

    if (pendingCloseEventData.value) {
      entryId = pendingCloseEventData.value.entryId;
      sessionId = pendingCloseEventData.value.sessionId;
      studyId = pendingCloseEventData.value.studyId;
      formId = pendingCloseEventData.value.formId;
    } else if (entrySessionId.value && form.value) {
      entryId = entry.value?.identifier || '';
      sessionId = entrySessionId.value;
      studyId = studyStore.selectedStudyId || form.value.studyId || '';
      formId = form.value.identifier;
    }

    if (sessionId && studyId && formId) {
      isEntryCloseEventSent.value = true;
      pendingCloseEventData.value = null;
      formStartTracked.value = false;

      MetricsService.recordEntryMetricsWithKeepAlive({
        entryId: entryId,
        sessionId: sessionId,
        studyId: studyId,
        formId: formId,
        event: MetricsEvent.FORM_ENTRY_CLOSE,
        timestamp: Date.now(),
        entryToken: entryToken.value,
        caseId: caseId.value
      });
    }

    cleanupListeners();
  };

  const setupEntryCloseListener = () => {
    const handleBeforeUnload = () => {
      sendEntryCloseEvent();
    };

    const handleVisibilityChange = async () => {
      if (
        isEntryCloseEventSent.value ||
        !entrySessionId.value ||
        !form.value?.identifier
      ) {
        return;
      }

      const event = document.hidden ?
        MetricsEvent.FORM_ENTRY_HIDDEN :
        MetricsEvent.FORM_ENTRY_VISIBLE;

      await MetricsService.recordEntryMetrics({
        entryId: entry.value?.identifier || '',
        sessionId: entrySessionId.value,
        studyId: studyStore.selectedStudyId || form.value.studyId || '',
        formId: form.value.identifier,
        event: event,
        timestamp: Date.now(),
        entryToken: entryToken.value,
        caseId: caseId.value
      }).catch(() => { });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };

  watch(
    [() => form.value, () => entrySessionId.value],
    ([newForm, newSessionId], [oldForm, oldSessionId]) => {
      const wasActive = !!(oldForm && oldSessionId);
      const isActive = !!(newForm && newSessionId);

      if (wasActive === isActive) {
        return;
      }

      cleanupListeners();

      if (isActive) {
        cleanupEntryCloseListener.value = setupEntryCloseListener();
      } else {
        formStartTracked.value = false;
      }
    }
  );

  onUnmounted(() => {
    sendEntryCloseEvent();
    cleanupListeners();
  });

  return {
    getForm,
    setEntryContext,
    clearEntryContext,
    getSignedUrl,
    submitEntry,
    downloadBlob,
    downloadDisplayCopy,
    deleteFile,
    showDiscardChangesDialog,
    cancelDiscardChangesDialog,
    discardEntryChanges,
    switchLangauge,
    lookForRequiredFields,
    setSectionExpanded,
    getSignedUrlForMatrixFiles,
    getFilesMetadata,
    deleteMatrixFieldFile,
    trackFormStart,
    sendEntryCloseEvent,
    entrySessionId,
    resolveFn,
    form,
    entry,
    entryLocked,
    getFormLoader,
    effectsSettled,
    formClosedError,
    formClosedMessage,
    anonymousResponsesNotAllowed,
    selectedFiles,
    isEntryPristine,
    openDiscardChangesAlert,
    selectedLanguage,
    entryTokenPreview,
    entryActivities,
    fetchEntrySignHistory,
    selectedMatrixFieldFiles,
    copiedEntryData,
    anonymousEntrySubmitError,
    entryDisableEdit,
    multipleEntriesError,
    restoredEntryData,
    resetRestoredEntryData,
    hideRestoredEntryDataMsg
  };
});
